import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '@/context/DataContext';

interface Result {
  type: string;
  title: string;
  sub: string;
  path: string;
  icon: string;
}

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { customers, quotes, projects } = useData();

  const results = useMemo<Result[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out: Result[] = [];

    customers.forEach((c) => {
      if (
        c.name.toLowerCase().includes(q) ||
        c.contactPerson.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        c.city.toLowerCase().includes(q)
      ) {
        out.push({
          type: 'Müşteri',
          title: c.name,
          sub: `${c.contactPerson} · ${c.city}`,
          path: `/customers/${c.id}`,
          icon: 'ri-team-line',
        });
      }
    });

    quotes.forEach((qt) => {
      if (qt.number.toLowerCase().includes(q) || qt.projectName.toLowerCase().includes(q)) {
        out.push({
          type: 'Teklif',
          title: qt.projectName,
          sub: `${qt.number} · ${qt.status}`,
          path: `/quotes/${qt.id}`,
          icon: 'ri-file-list-3-line',
        });
      }
    });

    projects.forEach((p) => {
      if (p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q)) {
        out.push({
          type: 'Proje',
          title: p.name,
          sub: `${p.id} · ${p.status}`,
          path: `/projects/${p.id}`,
          icon: 'ri-briefcase-line',
        });
      }
    });

    return out.slice(0, 8);
  }, [query, customers, quotes, projects]);

  const open = focused && query.trim().length > 0;

  const select = (path: string) => {
    navigate(path);
    setQuery('');
    setFocused(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative min-w-0 max-w-xl flex-1">
      <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 transition-colors focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-200">
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-search-line text-base text-foreground-400" />
        </span>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => window.setTimeout(() => setFocused(false), 150)}
          placeholder="Ara: firma, proje no, teklif no, telefon..."
          className="h-9 w-full min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
          >
            <i className="ri-close-circle-line" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-foreground-500">Sonuç bulunamadı</div>
          ) : (
            results.map((r) => (
              <button
                key={r.type + r.title}
                type="button"
                onClick={() => select(r.path)}
                className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                  <i className={r.icon} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground-900">{r.title}</span>
                  <span className="block truncate text-xs text-foreground-500">{r.sub}</span>
                </span>
                <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] font-semibold text-secondary-700">
                  {r.type}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}